import maplibregl from 'maplibre-gl';
import L from 'leaflet';
import { Protocol, PMTiles } from 'pmtiles';
import { db, CATEGORIES } from './db.js';
import { OSM_CATEGORIES, OSM_CATEGORY_KEYS, markerIcon } from './categories.js';

const MARKER_CATEGORY_ICONS = {
  Hospital: '🏥',
  Shop: '🛒',
  'Water Point': '💧',
  Fuel: '⛽',
  School: '🏫',
};

const MARKER_CATEGORY_COLORS = {
  Hospital: '#ef4444',
  Shop: '#f59e0b',
  'Water Point': '#3b82f6',
  Fuel: '#8b5cf6',
  School: '#10b981',
};
const leafletIconCache = new Map();

function categoryIconHtml(poi) {
  const key = poi.osmCategory || poi.category || 'amenity';
  const glyph = markerIcon(poi.osmValue || poi.category || key);
  const color = poi.color || OSM_CATEGORIES[key]?.color || '#3B82F6';
  return `<div class="m2-poi-marker" style="--poi-color:${color}"><span class="m2-poi-glyph">${glyph}</span></div>`;
}

function leafletCategoryIcon(poi) {
  const key = `${poi.osmCategory || poi.category || 'amenity'}:${poi.osmValue || ''}`;
  if (leafletIconCache.has(key)) return leafletIconCache.get(key);
  const icon = L.divIcon({
    className: 'm2-poi-icon',
    html: categoryIconHtml(poi),
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -14],
  });
  leafletIconCache.set(key, icon);
  return icon;
}

function categoryImageData(categoryKey) {
  const config = OSM_CATEGORIES[categoryKey] || OSM_CATEGORIES.amenity;
  return imageDataFor(config.icon || '📍', config.color);
}

function markerImageData(category) {
  return imageDataFor(MARKER_CATEGORY_ICONS[category] || '📍', MARKER_CATEGORY_COLORS[category] || '#64748b');
}

function imageDataFor(icon, color) {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, size, size);
  ctx.beginPath();
  ctx.arc(32, 32, 28, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  ctx.font = '28px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(icon, 32, 34);
  return { width: size, height: size, data: ctx.getImageData(0, 0, size, size).data };
}

function ensureCategoryImages() {
  if (!map) return;
  for (const key of OSM_CATEGORY_KEYS) {
    const id = `poi-${key}`;
    if (!map.hasImage(id)) {
      try {
        map.addImage(id, categoryImageData(key), { pixelRatio: 2 });
      } catch (error) {
        console.warn('[Map] category icon', key, error.message);
      }
    }
  }
  for (const cat of CATEGORIES) {
    const id = `marker-${cat}`;
    if (!map.hasImage(id)) {
      try {
        map.addImage(id, markerImageData(cat), { pixelRatio: 2 });
      } catch (error) {
        console.warn('[Map] marker icon', cat, error.message);
      }
    }
  }
}
 

const protocol = new Protocol();
maplibregl.addProtocol('pmtiles', protocol.tile);

const CATEGORY_COLORS = {
  Hospital: '#ef4444',
  Shop: '#f59e0b',
  'Water Point': '#3b82f6',
  Fuel: '#8b5cf6',
  School: '#10b981',
  default: '#64748b',
};

class OPFSSource {
  constructor(filename) {
    this.filename = filename;
  }

  getKey() {
    return `opfs:${this.filename}`;
  }

  async getBytes(offset, length, _signal, _path) {
    const root = await navigator.storage.getDirectory();
    const handle = await root.getFileHandle(this.filename);
    const file = await handle.getFile();
    const slice = file.slice(offset, offset + length);
    const buffer = await slice.arrayBuffer();
    return { data: new Uint8Array(buffer) };
  }
}

let map = null;
let fallbackMap = null;
let pmtilesInstance = null;
let activeCategories = new Set(CATEGORIES);
let pendingPOIs = [];
let pendingRoute = [];
let pendingRider = null;
let pendingCamera = null;
let pendingNavigationRoute = null;
let pendingDestination = null;
let fallbackRider = null;
let fallbackAccuracyCircle = null;
let fallbackRideRoute = null;
let fallbackNavigationRoute = null;
let fallbackDestination = null;
let fallbackPOIs = null;

export function getMap() {
  return map || fallbackMap;
}

function hasWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
  } catch {
    return false;
  }
}

function riderArrowImage() {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  ctx.translate(32, 32);
  ctx.beginPath();
  ctx.moveTo(0, -27);
  ctx.lineTo(18, 22);
  ctx.lineTo(0, 14);
  ctx.lineTo(-18, 22);
  ctx.closePath();
  ctx.fillStyle = '#facc15';
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#111827';
  ctx.stroke();
  return { width: 64, height: 64, data: ctx.getImageData(0, 0, 64, 64).data };
}

export function getCategoryColor(category) {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.default;
}

export function getActiveCategories() {
  return new Set(activeCategories);
}

export function setActiveCategories(cats) {
  activeCategories = new Set(cats);
  return loadMarkers();
}

async function fileExists(url) {
  try {
    const res = await fetch(url, { method: 'GET', cache: 'default' });
    return res.ok;
  } catch {
    return false;
  }
}

async function buildStyle(pmtilesKey) {
  const style = {
    version: 8,
    sources: {
      basemap: {
        type: 'vector',
        url: `pmtiles://${pmtilesKey}`,
        attribution: '',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': '#f8fafc' } },
      {
        id: 'water',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'water',
        paint: { 'fill-color': '#bfdbfe', 'fill-outline-color': '#93c5fd' },
      },
      {
        id: 'landuse',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'landuse',
        filter: ['in', ['get', 'class'], ['literal', ['park', 'forest', 'grass', 'school', 'hospital']]],
        paint: { 'fill-color': '#bbf7d0', 'fill-opacity': 0.6 },
      },
      {
        id: 'roads-motorway',
        type: 'line',
        source: 'basemap',
        'source-layer': 'roads',
        minzoom: 6,
        filter: ['==', ['get', 'class'], 'motorway'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#f87171', 'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1, 14, 4] },
      },
      {
        id: 'roads-major',
        type: 'line',
        source: 'basemap',
        'source-layer': 'roads',
        minzoom: 8,
        filter: ['in', ['get', 'class'], ['literal', ['primary', 'secondary', 'trunk']]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#fdba74', 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1, 16, 3] },
      },
      {
        id: 'roads-minor',
        type: 'line',
        source: 'basemap',
        'source-layer': 'roads',
        minzoom: 12,
        filter: ['in', ['get', 'class'], ['literal', ['tertiary', 'residential', 'unclassified', 'service']]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#e2e8f0', 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.5, 16, 2] },
      },
      {
        id: 'buildings',
        type: 'fill',
        source: 'basemap',
        'source-layer': 'buildings',
        minzoom: 13,
        paint: { 'fill-color': '#e2e8f0', 'fill-outline-color': '#cbd5e1' },
      },
      {
        id: 'places',
        type: 'symbol',
        source: 'basemap',
        'source-layer': 'places',
        minzoom: 6,
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 6, 10, 14, 14],
          'text-anchor': 'bottom-left',
          'text-offset': [0.2, 0],
          'text-variable-anchor': ['left', 'right', 'top', 'bottom'],
        },
        paint: { 'text-color': '#1e293b', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
      },
    ],
  };

  const hasGlyphs = await fileExists('/fonts/Noto Sans Regular/0-255.pbf');
  if (hasGlyphs) {
    style.glyphs = `${location.origin}/fonts/{fontstack}/{range}.pbf`;
  }

  const hasSprite = await fileExists('/sprites/sprite.json');
  if (hasSprite) {
    style.sprite = `${location.origin}/sprites/sprite`;
  }

  return style;
}

export async function initMap(containerId, filename) {
  if (map) {
    map.remove();
    map = null;
  }
  if (fallbackMap) {
    fallbackMap.remove();
    fallbackMap = null;
  }

  try {
    let header = { centerLon: 0, centerLat: 20, minZoom: 2 };

    // Escooter-nav-style: prefer Leaflet raster tiles by default. Use MapLibre
    // only when an offline PMTiles vector bundle is explicitly supplied.
    if (filename && hasWebGL()) {
      const source = new OPFSSource(filename);
      pmtilesInstance = new PMTiles(source);
      protocol.add(pmtilesInstance);
      const style = await buildStyle(source.getKey());
      header = await pmtilesInstance.getHeader();
      return initMaplibreMap(containerId, header, style);
    }

    return initLeafletMap(containerId, header);
  } catch (err) {
    console.warn('[Map] init unavailable:', err.message);
    throw err;
  }
}

function initMaplibreMap(containerId, header, style) {
  map = new maplibregl.Map({
    container: containerId,
    style,
    center: [header.centerLon || 0, header.centerLat || 0],
    zoom: header.minZoom || 2,
    localFontFamily: 'Noto Sans, sans-serif',
    attributionControl: false,
  });

  map.addControl(new maplibregl.NavigationControl(), 'bottom-right');
  map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');

  map.on('load', async () => {
    map.addSource('markers', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addSource('ride-route', { type: 'geojson', data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] } } });
    map.addSource('rider-position', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addSource('ride-pois', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addSource('navigation-route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addSource('navigation-destination', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    if (!map.hasImage('rider-arrow')) map.addImage('rider-arrow', riderArrowImage(), { pixelRatio: 2 });
    ensureCategoryImages();
    addMarkerLayers();
    addRideLayers();
    addNavigationLayers();
    setRideRoute(pendingRoute);
    setPOIs(pendingPOIs);
    if (pendingRider) setRiderPosition(pendingRider.lng, pendingRider.lat, pendingRider.heading);
    if (pendingNavigationRoute) setNavigationRoute(pendingNavigationRoute, pendingDestination);
    await loadMarkers();
    bindMarkerInteractions();
  });

  map.on('moveend', () => loadMarkers());
  map.on('zoomend', () => loadMarkers());

  return map;
}

function initLeafletMap(containerId, header) {
  fallbackMap = L.map(containerId, {
    zoomControl: false,
    preferCanvas: true,
    zoomSnap: 0.5,
    minZoom: 2,
    maxZoom: 20,
  }).setView([header.centerLat || 20, header.centerLon || 0], header.minZoom || 2);

  // CartoDB Voyager — clean, readable, modern basemap.
  L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
    maxZoom: 20,
    subdomains: 'abcd',
    attribution: '&copy; OpenStreetMap &copy; CARTO',
  }).addTo(fallbackMap);

  L.control.zoom({ position: 'bottomright' }).addTo(fallbackMap);

  // Trajectory line (traveled path) — gold with glow shadow.
  fallbackRideRoute = L.polyline([], {
    color: '#facc15',
    weight: 6,
    opacity: 0.9,
    lineCap: 'round',
    lineJoin: 'round',
  }).addTo(fallbackMap);

  // Navigation route — cyan with glow.
  fallbackNavigationRoute = L.polyline([], {
    color: '#22d3ee',
    weight: 8,
    opacity: 0.9,
    lineCap: 'round',
    lineJoin: 'round',
  }).addTo(fallbackMap);

  fallbackDestination = L.layerGroup().addTo(fallbackMap);
  fallbackPOIs = L.layerGroup().addTo(fallbackMap);

  // Apply any pending state from GPS that fired before map was ready.
  if (pendingRoute.length) setRideRoute(pendingRoute);
  if (pendingNavigationRoute) setNavigationRoute(pendingNavigationRoute, pendingDestination);
  if (pendingRider) setRiderPosition(pendingRider.lng, pendingRider.lat, pendingRider.heading);
  if (pendingCamera) {
    const z = pendingCamera.zoom != null ? pendingCamera.zoom : 16;
    fallbackMap.setView([pendingCamera.lat, pendingCamera.lng], z, { animate: false });
  }
  setPOIs(pendingPOIs);
  setMarkers(pendingMarkers);
  loadMarkers();

  // Fix tile rendering after layout settles (dashboard/sheet may shift container).
  setTimeout(() => fallbackMap.invalidateSize(), 100);
  setTimeout(() => fallbackMap.invalidateSize(), 500);
  window.addEventListener('resize', () => fallbackMap.invalidateSize());
  window.addEventListener('orientationchange', () => setTimeout(() => fallbackMap.invalidateSize(), 300));

  return fallbackMap;
}

function addRideLayers() {
  map.addLayer({
    id: 'ride-route-line',
    type: 'line',
    source: 'ride-route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#facc15', 'line-width': 5, 'line-opacity': 0.9 },
  });
  map.addLayer({
    id: 'rider-position',
    type: 'symbol',
    source: 'rider-position',
    layout: {
      'icon-image': 'rider-arrow',
      'icon-size': 0.8,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-rotate': ['get', 'heading'],
      'icon-rotation-alignment': 'map',
    },
  });
  map.addLayer({
    id: 'ride-poi-points',
    type: 'symbol',
    source: 'ride-pois',
    layout: {
      'icon-image': ['coalesce', ['get', 'iconId'], 'poi-amenity'],
      'icon-size': 0.7,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'text-field': ['step', ['zoom'], '', 14, ['get', 'name']],
      'text-size': 11,
      'text-offset': [0, 1.4],
      'text-anchor': 'top',
      'text-optional': true,
    },
    paint: {
      'text-color': '#0f172a',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.2,
    },
  });
}

function addNavigationLayers() {
  map.addLayer({
    id: 'navigation-route-line',
    type: 'line',
    source: 'navigation-route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#22d3ee', 'line-width': 7, 'line-opacity': 0.9 },
  });
  map.addLayer({
    id: 'navigation-destination',
    type: 'circle',
    source: 'navigation-destination',
    paint: { 'circle-color': '#f43f5e', 'circle-radius': 9, 'circle-stroke-color': '#ffffff', 'circle-stroke-width': 3 },
  });
}

export function setNavigationRoute(route, destination = null) {
  pendingNavigationRoute = route;
  pendingDestination = destination;
  if (fallbackMap) {
    fallbackNavigationRoute.setLatLngs(route?.coordinates?.map(([lng, lat]) => [lat, lng]) || []);
    fallbackDestination.clearLayers();
    if (destination) L.circleMarker([destination[1], destination[0]], { radius: 9, color: '#ffffff', weight: 3, fillColor: '#f43f5e', fillOpacity: 1 }).addTo(fallbackDestination);
    return;
  }
  const routeSource = map?.getSource('navigation-route');
  const destinationSource = map?.getSource('navigation-destination');
  if (!routeSource || !destinationSource) return;
  routeSource.setData({ type: 'FeatureCollection', features: route ? [{ type: 'Feature', properties: {}, geometry: route }] : [] });
  destinationSource.setData({
    type: 'FeatureCollection',
    features: destination ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: destination } }] : [],
  });
}

export function clearNavigationRoute() {
  setNavigationRoute(null, null);
}

export function fitNavigationRoute(route) {
  if (fallbackMap && route?.coordinates?.length) {
    const points = route.coordinates.map(([lng, lat]) => [lat, lng]);
    return fallbackMap.fitBounds(points, { padding: [80, 80], animate: true });
  }
  if (!map || !route?.coordinates?.length) return;
  const lngs = route.coordinates.map(([lng]) => lng);
  const lats = route.coordinates.map(([, lat]) => lat);
  map.fitBounds([[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]], { padding: 80, duration: 700 });
}

export function setRiderPosition(lng, lat, heading = 0, accuracy = null) {
  pendingRider = { lng, lat, heading, accuracy };
  if (fallbackMap) {
    // Accuracy circle — translucent blue ring showing GPS precision.
    if (fallbackAccuracyCircle) {
      fallbackAccuracyCircle.setLatLng([lat, lng]);
      if (accuracy != null) fallbackAccuracyCircle.setRadius(accuracy);
    } else if (accuracy != null && accuracy > 0) {
      fallbackAccuracyCircle = L.circle([lat, lng], {
        radius: accuracy,
        color: '#22d3ee',
        weight: 1,
        opacity: 0.4,
        fillColor: '#22d3ee',
        fillOpacity: 0.08,
      }).addTo(fallbackMap);
    }

    // Rider arrow marker — rotates with heading, glows.
    const icon = L.divIcon({
      className: 'leaflet-rider',
      html: `<div class="rider-arrow" style="transform:rotate(${heading}deg)"><svg width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="#0f1117" stroke="#22d3ee" stroke-width="2.5" opacity="0.9"/><path d="M16 4 L24 26 L16 21 L8 26 Z" fill="#facc15" stroke="#0f1117" stroke-width="1.5" stroke-linejoin="round"/></svg></div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
    if (!fallbackRider) {
      fallbackRider = L.marker([lat, lng], { icon, zIndexOffset: 1000, interactive: false }).addTo(fallbackMap);
    } else {
      fallbackRider.setIcon(icon).setLatLng([lat, lng]);
    }
    return;
  }
  const source = map?.getSource('rider-position');
  if (!source) return;
  source.setData({
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: { heading } }],
  });
}

export function setRideRoute(coordinates) {
  pendingRoute = coordinates;
  if (fallbackRideRoute) {
    fallbackRideRoute.setLatLngs(coordinates.map(([lng, lat]) => [lat, lng]));
    return;
  }
  const source = map?.getSource('ride-route');
  if (!source) return;
  source.setData({ type: 'Feature', geometry: { type: 'LineString', coordinates } });
}

export function setPOIs(pois) {
  pendingPOIs = pois;
  if (fallbackPOIs) {
    fallbackPOIs.clearLayers();
    pois.forEach((poi) => {
      const label = poi.name || poi.category || 'POI';
      L.marker([poi.lat, poi.lng], { icon: leafletCategoryIcon(poi), keyboard: false })
        .bindTooltip(`${markerIcon(poi.osmValue || poi.osmCategory || poi.category)} ${label}`, { direction: 'top', offset: [0, -10] })
        .addTo(fallbackPOIs);
    });
    return;
  }
  const source = map?.getSource('ride-pois');
  if (!source) return;
  ensureCategoryImages();
  source.setData({
    type: 'FeatureCollection',
    features: pois.map((poi) => {
      const osmCategory = poi.osmCategory || 'amenity';
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [poi.lng, poi.lat] },
        properties: {
          name: poi.name,
          category: poi.category,
          osmCategory,
          color: poi.color || '#38bdf8',
          iconId: `poi-${osmCategory}`,
          icon: markerIcon(poi.osmValue || osmCategory),
        },
      };
    }),
  });
}

export function setFollowCamera(enabled, lng, lat, heading = 0, zoom = null) {
  if (!enabled) return;
  pendingCamera = { lng, lat, heading, zoom };
  if (fallbackMap) {
    const targetZoom = zoom != null ? zoom : Math.max(fallbackMap.getZoom(), 15);
    return fallbackMap.setView([lat, lng], targetZoom, { animate: true });
  }
  if (map) map.easeTo({ center: [lng, lat], bearing: heading, zoom: zoom ?? undefined, duration: 250, essential: true });
}

function addMarkerLayers() {
  map.addLayer({
    id: 'marker-points',
    type: 'symbol',
    source: 'markers',
    layout: {
      'icon-image': ['coalesce', ['get', 'iconId'], 'marker-📍'],
      'icon-size': 0.7,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'text-field': ['step', ['zoom'], '', 15, ['get', 'name']],
      'text-size': 11,
      'text-offset': [0, 1.4],
      'text-anchor': 'top',
      'text-optional': true,
    },
    paint: {
      'text-color': '#0f172a',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.2,
    },
  });

  map.on('mouseenter', 'marker-points', () => (map.getCanvas().style.cursor = 'pointer'));
  map.on('mouseleave', 'marker-points', () => (map.getCanvas().style.cursor = ''));
}

function bindMarkerInteractions() {
  map.on('click', 'marker-points', (e) => {
    const f = e.features[0];
    const props = f.properties;
    new maplibregl.Popup({ offset: 8 })
      .setLngLat(f.geometry.coordinates)
      .setHTML(`
        <div style="color:#0f172a;font-size:13px;line-height:1.4">
          <strong>${props.name || props.category || 'Marker'}</strong>
          ${props.category ? `<div style="color:#64748b;font-size:11px;margin-top:2px">${props.category}</div>` : ''}
          ${props.description ? `<p style="margin-top:4px;font-size:11px">${props.description}</p>` : ''}
        </div>
      `)
      .addTo(map);
  });
}

export function getMapBounds() {
  const mapInstance = map || fallbackMap;
  if (!mapInstance) return null;
  if (typeof mapInstance.getBounds === 'function' && mapInstance.getBounds()?.getWest) {
    const b = mapInstance.getBounds();
    return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
  }
  if (typeof mapInstance.getBounds === 'function') {
    const b = mapInstance.getBounds();
    if (b?.getSouthWest) {
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      return [sw.lng, sw.lat, ne.lng, ne.lat];
    }
  }
  return null;
}

export async function loadMarkers(bbox = null) {
  if (!map || !map.getSource('markers')) return;
  const bounds = bbox || getMapBounds();
  if (!bounds) return;

  try {
    const rows = await db.getInBounds(bounds, activeCategories.size === CATEGORIES.length ? null : activeCategories);
    const limit = 20000;
    const limited = rows.slice(0, limit);
    if (rows.length > limit) {
      console.warn(`[Map] loaded ${limited.length}/${rows.length} markers in viewport; rest ignored for performance`);
    }

    setMarkers(limited);
  } catch (err) {
    console.error('[Map] loadMarkers failed', err);
    throw err;
  }
}

let fallbackMarkersLayer = null;
let pendingMarkers = [];

function markerLeafletIcon(category) {
  const icon = MARKER_CATEGORY_ICONS[category] || '📍';
  const color = MARKER_CATEGORY_COLORS[category] || '#64748b';
  return L.divIcon({
    className: 'm2-marker-icon',
    html: `<div class="m2-poi-marker" style="--poi-color:${color}"><span class="m2-poi-glyph">${icon}</span></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -14],
  });
}

export function setMarkers(markers) {
  pendingMarkers = markers;
  if (fallbackMap) {
    if (fallbackMarkersLayer) fallbackMap.removeLayer(fallbackMarkersLayer);
    fallbackMarkersLayer = L.layerGroup();
    markers.forEach((m) => {
      L.marker([m.lat, m.lng], { icon: markerLeafletIcon(m.category), keyboard: false })
        .bindPopup(`<strong>${m.name || m.category}</strong>${m.description ? `<br>${m.description}` : ''}`, { offset: [0, -10] })
        .addTo(fallbackMarkersLayer);
    });
    fallbackMarkersLayer.addTo(fallbackMap);
    return;
  }
  const source = map?.getSource('markers');
  if (!source) return;
  ensureCategoryImages();
  source.setData({
    type: 'FeatureCollection',
    features: markers.map((m) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [m.lng, m.lat] },
      properties: {
        id: m.id,
        name: m.name,
        category: m.category,
        description: m.description || '',
        iconId: `marker-${m.category}`,
      },
    })),
  });
}

export function watchMarkers(onError) {
  const mapInstance = map || fallbackMap;
  if (!mapInstance) return () => {};
  let timer = null;
  let lastKey = '';
  let inFlight = false;
  const refresh = () => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      if (inFlight) return;
      const bbox = getMapBounds();
      if (!bbox) return;
      const key = bbox.map((v) => Number(v).toFixed(2)).join(',');
      if (key === lastKey) return;
      lastKey = key;
      inFlight = true;
      try {
        await loadMarkers(bbox);
      } catch (err) {
        console.error('[Markers] viewport reload failed', err);
        onError?.(`Markers reload failed: ${err.message}`);
      } finally {
        inFlight = false;
      }
    }, 400);
  };
  mapInstance.on('moveend', refresh);
  if (typeof mapInstance.once === 'function') mapInstance.once('load', refresh);
  refresh();
  return () => {
    clearTimeout(timer);
    if (typeof mapInstance.off === 'function') mapInstance.off('moveend', refresh);
  };
}

const MAX_IMPORT_MARKERS = 10000;
const IMPORT_BATCH = 2000;

export async function addMarkersFromGeoJSON(geojson) {
  const features = geojson?.features || [];
  const rows = [];
  for (const f of features) {
    if (f.geometry?.type !== 'Point') continue;
    const [lng, lat] = f.geometry.coordinates;
    const props = f.properties || {};
    const category = CATEGORIES.includes(props.category) ? props.category : props.category || 'Water Point';
    rows.push({
      id: props.id || `${lat}-${lng}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      lat,
      lng,
      category,
      name: props.name || category,
      description: props.description || '',
    });
    if (rows.length >= MAX_IMPORT_MARKERS) break;
  }
  const trimmed = features.length > MAX_IMPORT_MARKERS;
  for (let i = 0; i < rows.length; i += IMPORT_BATCH) {
    await db.upsertBulk(rows.slice(i, i + IMPORT_BATCH));
  }
  await loadMarkers();
  return { count: rows.length, trimmed };
}

export async function clearAllMarkers() {
  await db.clearMarkers();
  return loadMarkers();
}
